import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Phone, Mail, ShieldCheck } from "lucide-react";
import { BUSINESS } from "@/lib/business";
import QuoteDialog from "@/components/QuoteDialog";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    {
        label: "Security Films",
        children: [
            { to: "/security-films/residential", label: "Residential" },
            { to: "/security-films/commercial", label: "Commercial" },
        ],
    },
    { to: "/security-cameras", label: "Security Cameras" },
    { to: "/blinds-shutters", label: "Blinds & Shutters" },
    { to: "/contact", label: "Contact Us" },
];

function TopBar() {
    return (
        <div className="bg-navy text-white text-sm" data-testid="top-utility-bar">
            <div className="container-x grid grid-cols-1 md:grid-cols-3 items-center py-2.5 gap-2">
                <span className="hidden md:inline-flex items-center gap-2 text-white/80 font-medium tracking-wide justify-self-start">
                    <ShieldCheck className="h-3.5 w-3.5 text-gold" />
                    Proudly Canadian Owned and Operated
                </span>
                <a
                    data-testid="topbar-email-link"
                    href={`mailto:${BUSINESS.email}`}
                    className="hidden md:inline-flex items-center gap-2 text-white/80 hover:text-gold transition-colors justify-self-center"
                >
                    <Mail className="h-3.5 w-3.5 text-gold" />
                    {BUSINESS.email}
                </a>
                <a
                    data-testid="topbar-phone-link"
                    href={BUSINESS.phoneHref}
                    className="inline-flex items-center gap-2 font-semibold tracking-wide hover:text-gold transition-colors justify-self-end"
                >
                    <Phone className="h-3.5 w-3.5 text-gold" />
                    {BUSINESS.phone}
                </a>
            </div>
        </div>
    );
}

function DesktopNav({ onOpenQuote }) {
    const [openDropdown, setOpenDropdown] = useState(false);
    return (
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((item) =>
                item.children ? (
                    <div
                        key={item.label}
                        className="relative"
                        onMouseEnter={() => setOpenDropdown(true)}
                        onMouseLeave={() => setOpenDropdown(false)}
                    >
                        <button
                            data-testid="nav-security-films-trigger"
                            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-navy hover:text-[#C9A961] transition-colors"
                        >
                            {item.label}
                            <ChevronDown className="h-4 w-4" />
                        </button>
                        {openDropdown && (
                            <div
                                className="absolute left-0 top-full pt-2 z-50"
                                data-testid="security-films-dropdown"
                            >
                                <div className="card-premium w-60 p-2">
                                    {item.children.map((c) => (
                                        <NavLink
                                            key={c.to}
                                            to={c.to}
                                            data-testid={`nav-dropdown-${c.label.toLowerCase()}`}
                                            className={({ isActive }) =>
                                                `block rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                                                    isActive
                                                        ? "bg-[#FDFCFB] text-[#C9A961]"
                                                        : "text-navy hover:bg-[#FDFCFB] hover:text-[#C9A961]"
                                                }`
                                            }
                                        >
                                            {c.label}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        end={item.to === "/"}
                        className={({ isActive }) =>
                            `px-4 py-2 text-sm font-medium transition-colors ${
                                isActive ? "text-[#C9A961]" : "text-navy hover:text-[#C9A961]"
                            }`
                        }
                    >
                        {item.label}
                    </NavLink>
                )
            )}
            <button
                data-testid="header-quote-button"
                onClick={onOpenQuote}
                className="ml-4 btn-gold !py-2.5 !px-5 text-sm"
            >
                Get a Free Quote
            </button>
        </nav>
    );
}

function MobileNav({ open, onClose, onOpenQuote }) {
    const [expand, setExpand] = useState(false);
    const location = useLocation();
    React.useEffect(() => {
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    if (!open) return null;
    return (
        <div
            className="lg:hidden fixed inset-0 z-[60] bg-white"
            data-testid="mobile-nav-panel"
        >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <Link to="/" onClick={onClose}>
                    <img src={BUSINESS.logo} alt="GTA Secure Inc." className="h-14 w-auto max-w-[260px] object-contain" />
                </Link>
                <button
                    data-testid="mobile-nav-close"
                    onClick={onClose}
                    className="p-2"
                >
                    <X className="h-6 w-6 text-navy" />
                </button>
            </div>
            <div className="px-6 py-6 space-y-1">
                {NAV.map((item) =>
                    item.children ? (
                        <div key={item.label}>
                            <button
                                data-testid="mobile-nav-films-toggle"
                                className="flex w-full items-center justify-between py-3 text-base font-semibold text-navy"
                                onClick={() => setExpand((v) => !v)}
                            >
                                {item.label}
                                <ChevronDown
                                    className={`h-4 w-4 transition-transform ${
                                        expand ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {expand && (
                                <div className="pl-4 border-l-2 border-gold">
                                    {item.children.map((c) => (
                                        <NavLink
                                            key={c.to}
                                            to={c.to}
                                            onClick={onClose}
                                            className="block py-2.5 text-sm font-medium text-navy"
                                        >
                                            {c.label}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={onClose}
                            className="block py-3 text-base font-semibold text-navy"
                        >
                            {item.label}
                        </NavLink>
                    )
                )}
                <div className="pt-6">
                    <button
                        data-testid="mobile-quote-button"
                        onClick={() => {
                            onClose();
                            onOpenQuote();
                        }}
                        className="btn-gold w-full"
                    >
                        Get a Free Quote
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Header() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <TopBar />
            <header
                className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100"
                data-testid="site-header"
            >
                <div className="container-x flex items-center justify-between py-2">
                    <Link
                        to="/"
                        className="flex items-center gap-3 flex-shrink-0"
                        data-testid="site-logo-link"
                    >
                        <img
                            src={BUSINESS.logo}
                            alt="GTA Secure Inc."
                            className="h-16 md:h-20 lg:h-24 w-auto max-w-[420px] md:max-w-[520px] lg:max-w-[620px] object-contain"
                        />
                    </Link>
                    <DesktopNav onOpenQuote={() => setQuoteOpen(true)} />
                    <button
                        data-testid="mobile-nav-open"
                        className="lg:hidden p-2"
                        onClick={() => setMobileOpen(true)}
                    >
                        <Menu className="h-6 w-6 text-navy" />
                    </button>
                </div>
            </header>
            <MobileNav
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                onOpenQuote={() => setQuoteOpen(true)}
            />
            <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
        </>
    );
}
